import React, { useState, useMemo, useEffect } from "react";
import { useCompany } from "@/hooks/useCompany";
import {
  CreateCompanyRequest,
  Company,
  CreateCompanyResponse,
} from "@/types/company";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import {
  companyFormLabels,
  companyValidationLabels,
  companyToastLabels,
  commonButtonLabels,
  commonFieldsLabels,
  commonMiscLabels,
} from "@/i18n";

interface CompanyFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (createdCompany?: Company) => void;
  initialOrgNumber?: string;
}

export const CompanyFormDialog: React.FC<CompanyFormDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialOrgNumber,
}) => {
  const [formData, setFormData] = useState<CreateCompanyRequest>({
    name: "",
    organisationNumber: initialOrgNumber || "",
    postalNumber: "",
    address: "",
    city: "",
    email: "",
    phone: "",
  });

  const { isNative } = useNativePlatform();
  const {
    createCompanyAsync,
    isCreatingCompany,
    createCompanyError,
    refetchCompanies,
  } = useCompany();

  // i18n labels
  const formLabels = companyFormLabels();
  const validationLabels = companyValidationLabels();
  const toastLabels = companyToastLabels();
  const buttonLabels = commonButtonLabels();
  const fieldLabels = commonFieldsLabels();
  const miscLabels = commonMiscLabels();

  // Update formData when initialOrgNumber changes
  useEffect(() => {
    if (initialOrgNumber && isOpen) {
      setFormData((prev) => ({
        ...prev,
        organisationNumber: initialOrgNumber,
      }));
    }
  }, [initialOrgNumber, isOpen]);

  // Validation functions
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string) => {
    return phone.trim().length >= 7;
  };

  const validatePostalNumber = (postal: string) => {
    return postal.trim().length <= 7;
  };

  const validateOrgNumber = (orgNumber: string) => {
    const trimmed = orgNumber.trim();
    // Swedish organization number: 10 digits (XXXXXX-XXXX) or 12 digits (XXXXXX-XXXX) with country code
    // Allow 10-13 characters to be flexible with formatting
    if (trimmed.length < 10 || trimmed.length > 13) {
      return false;
    }

    // Check if it contains only digits and one optional dash
    const orgNumberRegex = /^\d{6}-?\d{4}$|^\d{10}$/;
    return orgNumberRegex.test(trimmed);
  };

  // Field validation states
  const fieldErrors = useMemo(() => {
    return {
      email: formData.email.trim() !== "" && !validateEmail(formData.email),
      phone: formData.phone.trim() !== "" && !validatePhone(formData.phone),
      postalNumber:
        formData.postalNumber.trim() !== "" &&
        !validatePostalNumber(formData.postalNumber),
      organisationNumber:
        formData.organisationNumber.trim() !== "" &&
        !validateOrgNumber(formData.organisationNumber),
    };
  }, [formData]);

  // Check if all required fields are filled and valid
  const isFormValid = useMemo(() => {
    return (
      formData.name.trim() !== "" &&
      formData.organisationNumber.trim() !== "" &&
      formData.postalNumber.trim() !== "" &&
      formData.address.trim() !== "" &&
      formData.city.trim() !== "" &&
      formData.email.trim() !== "" &&
      formData.phone.trim() !== "" &&
      validateEmail(formData.email) &&
      validatePhone(formData.phone) &&
      validatePostalNumber(formData.postalNumber) &&
      validateOrgNumber(formData.organisationNumber)
    );
  }, [formData]);

  const handleInputChange = (
    field: keyof CreateCompanyRequest,
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (
      !formData.name.trim() ||
      !formData.organisationNumber.trim() ||
      !formData.postalNumber.trim() ||
      !formData.address.trim() ||
      !formData.city.trim() ||
      !formData.email.trim() ||
      !formData.phone.trim()
    ) {
      toast.error(validationLabels.allFieldsRequired);
      return;
    }

    // Field validation
    if (!validateEmail(formData.email)) {
      toast.error(validationLabels.invalidEmail);
      return;
    }

    if (!validatePhone(formData.phone)) {
      toast.error(validationLabels.phoneMinLength);
      return;
    }

    if (!validatePostalNumber(formData.postalNumber)) {
      toast.error(validationLabels.postalMaxLength);
      return;
    }

    if (!validateOrgNumber(formData.organisationNumber)) {
      toast.error(validationLabels.invalidOrgNumber);
      return;
    }

    try {
      const response: CreateCompanyResponse =
        await createCompanyAsync(formData);
      toast.success(toastLabels.createSuccess);

      // Create Company object from formData and response
      const createdCompany: Company = {
        _id: response.id,
        name: formData.name,
        organisationNumber: formData.organisationNumber,
        postalNumber: formData.postalNumber,
        address: formData.address,
        city: formData.city,
        email: formData.email,
        phone: formData.phone,
        status: "NEW_CONTACT",
        created_at: response.createdAt,
      };

      // Refetch companies in the background to ensure data is up to date
      refetchCompanies();

      // Return the created company immediately
      onSuccess(createdCompany);

      // Reset form
      setFormData({
        name: "",
        organisationNumber: initialOrgNumber || "",
        postalNumber: "",
        address: "",
        city: "",
        email: "",
        phone: "",
      });
    } catch (error: any) {
      // Extract error message from API response
      let errorMessage = toastLabels.createError;

      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.details?.error) {
        errorMessage = error.details.error;
      }

      // Check if it's a duplicate orgNumber error - always use i18n label regardless of backend language
      const lowerMessage = errorMessage.toLowerCase();
      if (
        lowerMessage.includes("organisationsnummer") ||
        lowerMessage.includes("organization number") ||
        lowerMessage.includes("orgnumber") ||
        lowerMessage.includes("org number")
      ) {
        if (
          lowerMessage.includes("existerar redan") ||
          lowerMessage.includes("already exists") ||
          lowerMessage.includes("redan taget") ||
          lowerMessage.includes("finns redan") ||
          lowerMessage.includes("already registered") ||
          lowerMessage.includes("already taken")
        ) {
          // Always use i18n label for duplicate org number error
          errorMessage = validationLabels.orgNumberAlreadyExists;
        }
      }

      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    if (!isCreatingCompany) {
      onClose();
      // Reset form when closing
      setFormData({
        name: "",
        organisationNumber: initialOrgNumber || "",
        postalNumber: "",
        address: "",
        city: "",
        email: "",
        phone: "",
      });
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      handleClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        nested={isNative}
        nestedUndimmed={isNative}
        sheetOpen={isOpen}
        expandable={false}
        className="sm:max-w-[600px] w-[95vw] max-w-[95vw] max-h-[90vh] overflow-y-auto native-sheet-form"
      >
        <DialogHeader>
          <DialogTitle>{formLabels.title}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Company Name */}
            <div className="space-y-2">
              <Label htmlFor="name">{formLabels.companyName} *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder={formLabels.companyNamePlaceholder}
                required
              />
            </div>

            {/* Organization Number */}
            <div className="space-y-2">
              <Label htmlFor="organisationNumber">
                {formLabels.organizationNumber} *
              </Label>
              <Input
                id="organisationNumber"
                value={formData.organisationNumber}
                onChange={(e) =>
                  handleInputChange("organisationNumber", e.target.value)
                }
                placeholder={formLabels.organizationNumberPlaceholder}
                required
                className={
                  fieldErrors.organisationNumber ? "border-red-500" : ""
                }
              />
              {fieldErrors.organisationNumber && (
                <p className="text-red-500 text-sm">
                  {validationLabels.invalidOrgNumber}
                </p>
              )}
            </div>

            {/* Postal Number */}
            <div className="space-y-2">
              <Label htmlFor="postalNumber">{fieldLabels.postalNumber} *</Label>
              <Input
                id="postalNumber"
                value={formData.postalNumber}
                onChange={(e) =>
                  handleInputChange("postalNumber", e.target.value)
                }
                placeholder={formLabels.postalNumberPlaceholder}
                required
                className={fieldErrors.postalNumber ? "border-red-500" : ""}
              />
              {fieldErrors.postalNumber && (
                <p className="text-red-500 text-sm">
                  {validationLabels.postalMaxLength}
                </p>
              )}
            </div>

            {/* City */}
            <div className="space-y-2">
              <Label htmlFor="city">{fieldLabels.city} *</Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => handleInputChange("city", e.target.value)}
                placeholder={formLabels.cityPlaceholder}
                required
              />
            </div>

            {/* Address */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="address">{fieldLabels.address} *</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => handleInputChange("address", e.target.value)}
                placeholder={formLabels.addressPlaceholder}
                required
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">{miscLabels.email} *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder={formLabels.emailPlaceholder}
                required
                className={fieldErrors.email ? "border-red-500" : ""}
              />
              {fieldErrors.email && (
                <p className="text-red-500 text-sm">
                  {validationLabels.invalidEmail}
                </p>
              )}
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone">{miscLabels.phone} *</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => handleInputChange("phone", e.target.value)}
                placeholder={formLabels.phonePlaceholder}
                required
                className={fieldErrors.phone ? "border-red-500" : ""}
              />
              {fieldErrors.phone && (
                <p className="text-red-500 text-sm">
                  {validationLabels.phoneMinLength}
                </p>
              )}
            </div>
          </div>

          {/* Error Display */}
          {createCompanyError &&
            (() => {
              // Extract and translate error message
              let errorMessage =
                createCompanyError.message ||
                toastLabels.createError ||
                "Kunde inte skapa företag";
              const lowerMessage = errorMessage.toLowerCase();

              // Check if it's a duplicate orgNumber error - always use i18n label
              if (
                lowerMessage.includes("organisationsnummer") ||
                lowerMessage.includes("organization number") ||
                lowerMessage.includes("orgnumber") ||
                lowerMessage.includes("org number")
              ) {
                if (
                  lowerMessage.includes("existerar redan") ||
                  lowerMessage.includes("already exists") ||
                  lowerMessage.includes("redan taget") ||
                  lowerMessage.includes("finns redan") ||
                  lowerMessage.includes("already registered") ||
                  lowerMessage.includes("already taken")
                ) {
                  errorMessage = validationLabels.orgNumberAlreadyExists;
                }
              }

              return <div className="text-red-500 text-sm">{errorMessage}</div>;
            })()}

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isCreatingCompany}
            >
              {buttonLabels.cancel}
            </Button>
            <Button type="submit" disabled={isCreatingCompany || !isFormValid}>
              {isCreatingCompany ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {miscLabels.adding}
                </>
              ) : (
                miscLabels.add
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
